import * as THREE from 'three';
import { ProjectilePool } from '../weapons/ProjectilePool';
import { EventBus } from '../core/EventBus';

export interface SniperTowerConfig {
  position: THREE.Vector3;
  targetRef: THREE.Object3D;
  health?: number;
  damage?: number;
  range?: number;
  chargeTime?: number;
}

const DEFAULT_CONFIG = {
  health: 120,
  damage: 40,
  range: 80,
  chargeTime: 2.0, // seconds to charge before firing
};

let towerIdCounter = 0;

export class SniperTower {
  public readonly id: string;
  public readonly mesh: THREE.Group;

  private config: Required<SniperTowerConfig>;
  private projectilePool: ProjectilePool;
  private scene: THREE.Scene;
  private health: number;
  private isDestroyed: boolean = false;

  // Aiming
  private headGroup!: THREE.Group;
  private aimAngle: number = 0;
  private aimPitch: number = 0;

  // Firing state
  private isCharging: boolean = false;
  private chargeProgress: number = 0;
  private cooldownTimer: number = 0;
  private readonly COOLDOWN_TIME = 4.0; // seconds between shots

  // Laser sight
  private laserLine!: THREE.Line;

  constructor(
    scene: THREE.Scene,
    projectilePool: ProjectilePool,
    config: SniperTowerConfig
  ) {
    this.id = `sniper_${towerIdCounter++}`;
    this.scene = scene;
    this.config = { ...DEFAULT_CONFIG, ...config } as Required<SniperTowerConfig>;
    this.projectilePool = projectilePool;
    this.health = this.config.health;

    // Create mesh
    this.mesh = this.createTowerMesh();
    this.mesh.position.copy(config.position);
    scene.add(this.mesh);

    // Create laser sight
    this.createLaserSight();

    // Set up hit detection
    this.mesh.userData = {
      id: this.id,
      type: 'sniper',
      health: this.health,
      hitRadius: 1.5,
      points: 350,
      isEnemy: true,
      entity: this,
    };
  }

  private createTowerMesh(): THREE.Group {
    const group = new THREE.Group();

    // Base platform
    const baseGeom = new THREE.CylinderGeometry(2, 2.5, 0.5, 8);
    const baseMat = new THREE.MeshStandardMaterial({
      color: 0x1a1a2e,
      metalness: 0.8,
      roughness: 0.3,
    });
    const base = new THREE.Mesh(baseGeom, baseMat);
    base.position.y = 0.25;
    group.add(base);

    // Tower body (tall pillar)
    const pillarGeom = new THREE.CylinderGeometry(0.8, 1.2, 8, 8);
    const pillarMat = new THREE.MeshStandardMaterial({
      color: 0x2a2a4e,
      metalness: 0.9,
      roughness: 0.2,
    });
    const pillar = new THREE.Mesh(pillarGeom, pillarMat);
    pillar.position.y = 4.5;
    group.add(pillar);

    // Glowing rings along pillar
    const ringMat = new THREE.MeshStandardMaterial({
      color: 0x00ffff,
      emissive: 0x00ffff,
      emissiveIntensity: 0.8,
    });
    for (let i = 0; i < 4; i++) {
      const ringGeom = new THREE.TorusGeometry(1, 0.05, 8, 16);
      const ring = new THREE.Mesh(ringGeom, ringMat);
      ring.rotation.x = Math.PI / 2;
      ring.position.y = 1.5 + i * 2;
      group.add(ring);
    }

    // Head/turret group (rotates to aim)
    this.headGroup = new THREE.Group();
    this.headGroup.position.y = 8.5;

    // Head housing
    const headGeom = new THREE.BoxGeometry(1.5, 1, 2);
    const headMat = new THREE.MeshStandardMaterial({
      color: 0x3a3a5e,
      metalness: 0.9,
      roughness: 0.1,
    });
    const head = new THREE.Mesh(headGeom, headMat);
    this.headGroup.add(head);

    // Sniper barrel
    const barrelGeom = new THREE.CylinderGeometry(0.1, 0.15, 4, 8);
    const barrelMat = new THREE.MeshStandardMaterial({
      color: 0x4a4a6e,
      metalness: 0.95,
      roughness: 0.05,
    });
    const barrel = new THREE.Mesh(barrelGeom, barrelMat);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.z = 2.5;
    barrel.name = 'barrel';
    this.headGroup.add(barrel);

    // Scope
    const scopeGeom = new THREE.CylinderGeometry(0.12, 0.12, 0.8, 8);
    const scope = new THREE.Mesh(scopeGeom, barrelMat);
    scope.rotation.z = Math.PI / 2;
    scope.position.set(0, 0.3, 0.5);
    this.headGroup.add(scope);

    // Scope lens (glows when charging)
    const lensGeom = new THREE.CircleGeometry(0.1, 16);
    const lensMat = new THREE.MeshStandardMaterial({
      color: 0xff0000,
      emissive: 0xff0000,
      emissiveIntensity: 0.5,
      side: THREE.DoubleSide,
    });
    const lens = new THREE.Mesh(lensGeom, lensMat);
    lens.position.set(0.4, 0.3, 0.5);
    lens.rotation.y = Math.PI / 2;
    lens.name = 'lens';
    this.headGroup.add(lens);

    // Muzzle tip
    const muzzleGeom = new THREE.CylinderGeometry(0.08, 0.12, 0.3, 8);
    const muzzle = new THREE.Mesh(muzzleGeom, barrelMat);
    muzzle.rotation.x = Math.PI / 2;
    muzzle.position.z = 4.6;
    muzzle.name = 'muzzle';
    this.headGroup.add(muzzle);

    group.add(this.headGroup);

    return group;
  }

  private createLaserSight(): void {
    const laserMat = new THREE.LineBasicMaterial({
      color: 0xff0000,
      transparent: true,
      opacity: 0,
    });

    const points = [new THREE.Vector3(), new THREE.Vector3(0, 0, 100)];
    const laserGeom = new THREE.BufferGeometry().setFromPoints(points);

    this.laserLine = new THREE.Line(laserGeom, laserMat);
    this.laserLine.frustumCulled = false;
    this.scene.add(this.laserLine);
  }

  public update(dt: number): void {
    if (this.isDestroyed) return;

    const target = this.config.targetRef;
    if (!target) return;

    const targetPos = target.position.clone();
    const headWorldPos = this.mesh.position.clone().add(new THREE.Vector3(0, 8.5, 0));
    const toTarget = targetPos.clone().sub(headWorldPos);
    const distToTarget = toTarget.length();

    // Track target with head (slow tracking)
    const horizontalDist = new THREE.Vector2(toTarget.x, toTarget.z).length();
    const targetAimAngle = Math.atan2(toTarget.x, toTarget.z);
    const targetAimPitch = Math.atan2(toTarget.y, horizontalDist);

    // Smooth rotation
    let angleDiff = targetAimAngle - this.aimAngle;
    while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
    while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;

    const trackSpeed = this.isCharging ? 0.5 : 1.5; // Slower when charging
    this.aimAngle += angleDiff * dt * trackSpeed;
    this.aimPitch += (targetAimPitch - this.aimPitch) * dt * trackSpeed;

    // Clamp pitch
    this.aimPitch = Math.max(-0.5, Math.min(0.3, this.aimPitch));

    // Apply rotation
    this.headGroup.rotation.y = this.aimAngle;
    this.headGroup.rotation.x = -this.aimPitch;

    // Update cooldown
    if (this.cooldownTimer > 0) {
      this.cooldownTimer -= dt;
      this.updateLaser(0);
      return;
    }

    // Check if target in range and roughly aimed
    const isAimed = Math.abs(angleDiff) < 0.1;
    const inRange = distToTarget < this.config.range;

    if (inRange && isAimed) {
      // Start or continue charging
      if (!this.isCharging) {
        this.isCharging = true;
        this.chargeProgress = 0;
      }

      this.chargeProgress += dt;
      this.updateLaser(this.chargeProgress / this.config.chargeTime);
      this.updateChargingVisuals();

      if (this.chargeProgress >= this.config.chargeTime) {
        this.fire(targetPos);
        this.isCharging = false;
        this.chargeProgress = 0;
        this.cooldownTimer = this.COOLDOWN_TIME;
      }
    } else {
      // Reset charging if target moves out of aim
      if (this.isCharging && Math.abs(angleDiff) > 0.3) {
        this.isCharging = false;
        this.chargeProgress = 0;
        this.updateLaser(0);
      }
    }
  }

  private updateLaser(intensity: number): void {
    const mat = this.laserLine.material as THREE.LineBasicMaterial;
    mat.opacity = intensity * 0.8;

    if (intensity > 0) {
      // Get muzzle position
      const muzzle = this.headGroup.getObjectByName('muzzle');
      if (muzzle) {
        const start = new THREE.Vector3();
        muzzle.getWorldPosition(start);

        // Get aim direction
        const direction = new THREE.Vector3(0, 0, 1);
        direction.applyQuaternion(this.headGroup.quaternion);
        direction.applyQuaternion(this.mesh.quaternion);

        const end = start.clone().addScaledVector(direction, 100);

        const positions = this.laserLine.geometry.attributes.position;
        positions.setXYZ(0, start.x, start.y, start.z);
        positions.setXYZ(1, end.x, end.y, end.z);
        positions.needsUpdate = true;
      }
    }
  }

  private updateChargingVisuals(): void {
    const lens = this.headGroup.getObjectByName('lens') as THREE.Mesh;
    if (lens && lens.material) {
      const mat = lens.material as THREE.MeshStandardMaterial;
      const chargeRatio = this.chargeProgress / this.config.chargeTime;
      mat.emissiveIntensity = 0.5 + chargeRatio * 4;

      // Pulse effect when almost ready
      if (chargeRatio > 0.8) {
        const pulse = Math.sin(this.chargeProgress * 30) * 0.5 + 0.5;
        mat.emissiveIntensity = 3 + pulse * 2;
      }
    }
  }

  private fire(targetPos: THREE.Vector3): void {
    const muzzle = this.headGroup.getObjectByName('muzzle');
    if (!muzzle) return;

    const muzzlePos = new THREE.Vector3();
    muzzle.getWorldPosition(muzzlePos);

    // Very accurate shot
    const direction = targetPos.clone().sub(muzzlePos).normalize();

    // Fire powerful, fast projectile
    this.projectilePool.spawn(
      muzzlePos,
      direction,
      120, // Very fast
      this.config.damage,
      new THREE.Color(0xff0000)
    );

    // Emit firing event
    EventBus.emit('weapon:fired', {
      weaponType: 'Sniper',
      ammoLeft: -1,
      muzzlePosition: { x: muzzlePos.x, y: muzzlePos.y, z: muzzlePos.z },
      direction: { x: direction.x, y: direction.y, z: direction.z },
    });

    // Reset lens glow
    const lens = this.headGroup.getObjectByName('lens') as THREE.Mesh;
    if (lens && lens.material) {
      (lens.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.5;
    }
  }

  public takeDamage(damage: number): void {
    if (this.isDestroyed) return;

    this.health -= damage;
    this.mesh.userData.health = this.health;

    // Emit damage event
    EventBus.emit('target:damaged', {
      id: this.id,
      damage,
      healthRemaining: this.health,
    });

    // Flash effect
    this.flashDamage();

    // Interrupt charging when hit
    if (this.isCharging) {
      this.chargeProgress = Math.max(0, this.chargeProgress - 0.5);
    }

    if (this.health <= 0) {
      this.destroy();
    }
  }

  private flashDamage(): void {
    const head = this.headGroup.children[0] as THREE.Mesh;
    if (head && head.material) {
      const mat = head.material as THREE.MeshStandardMaterial;
      const originalColor = mat.emissive.clone();
      mat.emissive.setHex(0xffffff);
      mat.emissiveIntensity = 2;

      setTimeout(() => {
        mat.emissive.copy(originalColor);
        mat.emissiveIntensity = 0;
      }, 100);
    }
  }

  private destroy(): void {
    this.isDestroyed = true;

    // Remove laser
    if (this.laserLine.parent) {
      this.laserLine.parent.remove(this.laserLine);
    }

    // Emit destroyed event
    EventBus.emit('target:destroyed', {
      id: this.id,
      position: {
        x: this.mesh.position.x,
        y: this.mesh.position.y,
        z: this.mesh.position.z,
      },
      points: this.mesh.userData.points,
    });

    // Remove mesh
    if (this.mesh.parent) {
      this.mesh.parent.remove(this.mesh);
    }
  }

  public isAlive(): boolean {
    return !this.isDestroyed;
  }

  public getPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }

  public getMesh(): THREE.Group {
    return this.mesh;
  }
}
